// A4 — Clientes del tenant (CRUD + es_principal único + soft-delete con razón).

import { useState } from 'react';
import { toast } from 'sonner';
import type { AdminCliente } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import {
  useAdminClientes,
  useCreateAdminCliente,
  useUpdateAdminCliente,
} from '../../lib/api/queries';

export function A4ClientesPage() {
  const { data, isLoading, isError } = useAdminClientes();
  const [crear, setCrear] = useState(false);
  const [editar, setEditar] = useState<AdminCliente | null>(null);

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Clientes</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">Maestro de clientes del tenant</p>
          </div>
          <Button onClick={() => setCrear(true)}>Nuevo cliente</Button>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Cliente</TH>
                <TH>NIT</TH>
                <TH>SAP</TH>
                <TH align="center">Principal</TH>
                <TH align="center">Estado</TH>
                <TH align="right" />
              </TR>
            </THead>
            <TBody>
              {data.clientes.map((c) => (
                <TR key={c.id}>
                  <TD className="font-medium text-fg">{c.nombre}</TD>
                  <TD className="text-fg-secondary">{c.nit ?? '—'}</TD>
                  <TD className="text-fg-secondary">{c.codigo_externo_sap ?? '—'}</TD>
                  <TD align="center">
                    {c.es_principal ? <Badge tone="accent">Principal</Badge> : ''}
                  </TD>
                  <TD align="center">
                    <Badge tone={c.activo ? 'ok' : 'neutral'}>
                      {c.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <Button size="sm" variant="ghost" onClick={() => setEditar(c)}>
                      Editar
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
      {crear && <CrearDialog onClose={() => setCrear(false)} />}
      {editar && <EditarDialog cliente={editar} onClose={() => setEditar(null)} />}
    </RoleLayout>
  );
}

function CrearDialog({ onClose }: { onClose: () => void }) {
  const crear = useCreateAdminCliente();
  const [f, setF] = useState({ nombre: '', nit: '', codigo_externo_sap: '', es_principal: false });
  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo cliente</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Campo label="Nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
          <Campo label="NIT" value={f.nit} onChange={(v) => setF({ ...f, nit: v })} />
          <Campo
            label="Código SAP"
            value={f.codigo_externo_sap}
            onChange={(v) => setF({ ...f, codigo_externo_sap: v })}
          />
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.es_principal}
              onChange={(e) => setF({ ...f, es_principal: e.target.checked })}
            />
            Cliente principal
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={crear.isPending || f.nombre.trim() === ''}
            onClick={() =>
              crear.mutate(
                {
                  nombre: f.nombre.trim(),
                  nit: f.nit.trim() || null,
                  codigo_externo_sap: f.codigo_externo_sap.trim() || null,
                  es_principal: f.es_principal,
                },
                {
                  onSuccess: () => {
                    toast.success('Cliente creado');
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditarDialog({ cliente, onClose }: { cliente: AdminCliente; onClose: () => void }) {
  const update = useUpdateAdminCliente();
  const [f, setF] = useState({
    nombre: cliente.nombre,
    nit: cliente.nit ?? '',
    codigo_externo_sap: cliente.codigo_externo_sap ?? '',
    es_principal: cliente.es_principal,
    activo: cliente.activo,
    razon: '',
  });
  const desactivando = cliente.activo && !f.activo;
  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar {cliente.nombre}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Campo label="Nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
          <Campo label="NIT" value={f.nit} onChange={(v) => setF({ ...f, nit: v })} />
          <Campo
            label="Código SAP"
            value={f.codigo_externo_sap}
            onChange={(v) => setF({ ...f, codigo_externo_sap: v })}
          />
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.es_principal}
              onChange={(e) => setF({ ...f, es_principal: e.target.checked })}
            />
            Cliente principal
          </label>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.activo}
              onChange={(e) => setF({ ...f, activo: e.target.checked })}
            />
            Activo
          </label>
          {desactivando && (
            <Campo
              label="Razón de desactivación (≥20)"
              value={f.razon}
              onChange={(v) => setF({ ...f, razon: v })}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={update.isPending || (desactivando && f.razon.trim().length < 20)}
            onClick={() =>
              update.mutate(
                {
                  id: cliente.id,
                  body: {
                    nombre: f.nombre.trim(),
                    nit: f.nit.trim() || null,
                    codigo_externo_sap: f.codigo_externo_sap.trim() || null,
                    es_principal: f.es_principal,
                    activo: f.activo,
                    ...(desactivando ? { razon: f.razon.trim() } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    toast.success('Cliente actualizado');
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
